import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { apiSlice } from '../api/apiSlice';
import { RootState } from '../types';

// Define types
export interface Employee {
  _id: string;
  name: string;
  email: string;
  role: string;
  userId?: string | null;
  teamName?: string | null;
  managerName?: string | null;
  managerUserId?: string | null;
  jrAeUserId?: string | null;
  department?: {
    _id: string;
    id?: string;
    name?: string;
    isSalesTeam?: boolean;
  } | null;
  ptoAllotment: number;
  sickTimeAllotment: number;
  wfhAllotment: number;
  wfhEligible: boolean;
  vpName?: string | null;
  ptoUsed: number;
  sickTimeUsed: number;
  wfhUsed: number;
  employmentStatus: 'active' | 'terminated' | 'resigned_regrettable' | 'resigned_unregrettable';
  terminationDate?: string | null;
  wfhLeadsApproved: boolean;
  startDate?: string | null;
  customAllotment?: boolean;
  createdAt: string;
  updatedAt: string;
}

export type EmployeeMutationInput = Partial<Omit<Employee, 'department'>> & {
  department?: Employee['department'] | string | null;
};

export interface EmployeeState {
  selectedEmployee: Employee | null;
  filter: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

// Initial state
const initialState: EmployeeState = {
  selectedEmployee: null,
  filter: '',
  sortBy: 'name',
  sortOrder: 'asc',
};

// Create the slice
const employeeSlice = createSlice({
  name: 'employees',
  initialState,
  reducers: {
    setSelectedEmployee: (state, action: PayloadAction<Employee | null>) => {
      state.selectedEmployee = action.payload;
    },
    setFilter: (state, action: PayloadAction<string>) => {
      state.filter = action.payload;
    },
    setSortBy: (state, action: PayloadAction<string>) => {
      state.sortBy = action.payload;
    },
    setSortOrder: (state, action: PayloadAction<'asc' | 'desc'>) => {
      state.sortOrder = action.payload;
    },
  },
});

// Export actions and reducer
export const { setSelectedEmployee, setFilter, setSortBy, setSortOrder } = employeeSlice.actions;
export default employeeSlice.reducer;

// Selectors
export const selectSelectedEmployee = (state: RootState) => state.employees.selectedEmployee;
export const selectEmployeeFilter = (state: RootState) => state.employees.filter;
export const selectEmployeeSortBy = (state: RootState) => state.employees.sortBy;
export const selectEmployeeSortOrder = (state: RootState) => state.employees.sortOrder;

// API endpoints
export const employeeApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getEmployees: builder.query<Employee[], void>({
      query: () => '/employees',
      transformResponse: (response: { success: boolean; data: Employee[] }) => response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: 'Employee' as const, id: _id })),
              { type: 'Employee', id: 'LIST' },
            ]
          : [{ type: 'Employee', id: 'LIST' }],
    }),
    getEmployeeById: builder.query<Employee, string>({
      query: (id) => `/employees/${id}`,
      transformResponse: (response: { success: boolean; data: Employee }) => response.data,
      providesTags: (result, error, id) => [{ type: 'Employee', id }],
    }),
    getEmployeeTimeOffSummary: builder.query<any, string>({
      query: (id) => `/employees/${id}/time-off`,
      transformResponse: (response: { success: boolean; data: any }) => response.data,
      providesTags: (result, error, id) => [{ type: 'Employee', id }],
    }),
    addEmployee: builder.mutation<Employee, EmployeeMutationInput>({
      query: (employee) => ({
        url: '/employees',
        method: 'POST',
        body: employee,
      }),
      transformResponse: (response: { success: boolean; data: Employee }) => response.data,
      invalidatesTags: [{ type: 'Employee', id: 'LIST' }],
    }),
    updateEmployee: builder.mutation<Employee, { id: string; employee: EmployeeMutationInput }>({
      query: ({ id, employee }) => ({
        url: `/employees/${id}`,
        method: 'PUT',
        body: employee,
      }),
      transformResponse: (response: { success: boolean; data: Employee }) => response.data,
      invalidatesTags: (result, error, { id }) => [
        { type: 'Employee', id },
        { type: 'Employee', id: 'LIST' },
      ],
    }),
    deleteEmployee: builder.mutation<void, string>({
      query: (id) => ({
        url: `/employees/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Employee', id: 'LIST' }],
    }),
    getCSVTemplate: builder.query<string, void>({
      query: () => ({
        url: '/employees/csv-template',
        responseHandler: (response) => response.text(),
      }),
    }),
    importEmployees: builder.mutation<any, FormData>({
      query: (formData) => ({
        url: '/employees/import-csv',
        method: 'POST',
        body: formData,
      }),
      transformResponse: (response: { success: boolean; data: any }) => response.data,
      invalidatesTags: [{ type: 'Employee', id: 'LIST' }],
    }),
  }),
});

// Export hooks
export const {
  useGetEmployeesQuery,
  useGetEmployeeByIdQuery,
  useGetEmployeeTimeOffSummaryQuery,
  useAddEmployeeMutation,
  useUpdateEmployeeMutation,
  useDeleteEmployeeMutation,
  useGetCSVTemplateQuery,
  useImportEmployeesMutation,
} = employeeApiSlice;
